import React, { useState, useEffect } from "react";
import { Description, GetApp, InsertDriveFile } from "@mui/icons-material";
import apiService from "../../services/apiService";

const ContractsTab = ({ loanId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loanId) {
      fetchDocuments();
    }
  }, [loanId]);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.post(
        `${process.env.REACT_APP_API_HOST_DOCUMENTS}/get-documents`,
        {
          section: "loanAgreements",
          entityId: loanId
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      } else {
        setError("Failed to load contracts");
      }
    } catch (err) {
      console.error("Error fetching contracts:", err);
      setError("An error occurred while loading contracts");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return "N/A";
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
  };

  const handleDownload = (documentId) => {
    const downloadUrl = `${process.env.REACT_APP_API_HOST_DOCUMENTS}/public/document/${documentId}/download`;
    window.open(downloadUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-gray-600">Loading contracts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-12 text-center">
        <Description className="mx-auto text-red-400 mb-4" style={{ fontSize: 64 }} />
        <h3 className="text-lg font-semibold text-red-700 mb-2">Error Loading Contracts</h3>
        <p className="text-red-500">{error}</p>
        <button
          onClick={fetchDocuments}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="p-12 text-center">
        <InsertDriveFile className="mx-auto text-gray-400 mb-4" style={{ fontSize: 64 }} />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Contracts Available</h3>
        <p className="text-gray-500">
          No contract documents have been uploaded for this loan yet.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Contracts & Documents ({documents.length})
        </h3>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {documents.map((doc) => (
          <div
            key={doc._id}
            className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow hover:border-blue-300"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Description className="text-blue-600" style={{ fontSize: 28 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-gray-900 text-sm truncate">
                    {doc.documentType?.label || doc.documentType?.name || "Document"}
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(doc.createdAt)}
                  </p>
                </div>
              </div>
            </div>

            {doc.meta?.fileName && (
              <div className="mb-3">
                <p className="text-xs text-gray-600 truncate" title={doc.meta.fileName}>
                  {doc.meta.fileName}
                </p>
              </div>
            )}

            {doc.meta?.fileSize && (
              <div className="mb-3">
                <p className="text-xs text-gray-500">
                  Size: {formatFileSize(doc.meta.fileSize)}
                </p>
              </div>
            )}

            {doc.status && (
              <div className="mb-3">
                <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                  doc.status === 'completed' || doc.status === 'signed'
                    ? 'bg-green-100 text-green-800'
                    : doc.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {doc.status}
                </span>
              </div>
            )}

            <div className="flex gap-2 pt-3 border-t border-gray-200">
              <button
                onClick={() => handleDownload(doc._id)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
              >
                <GetApp fontSize="small" />
                Download
              </button>
            </div>

            {doc.meta?.notes && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-600 line-clamp-2">
                  {doc.meta.notes}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContractsTab;
